import {useEffect, useState} from 'react'
import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {BRAND, BRAND_NAME} from '#/lib/brand'
import {BrandSsoIcon} from '#/lib/brand/BrandMarkSvg'
import {startCooeyLogin} from '#/lib/cooey/handoff'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {useKawaiiMode} from '#/state/preferences/kawaii'
import {ErrorBoundary} from '#/view/com/util/ErrorBoundary'
import {Logo} from '#/view/icons/Logo'
import {
  AppClipOverlay,
  postAppClipMessage,
} from '#/screens/StarterPack/StarterPackLandingScreen'
import {atoms as a, useTheme} from '#/alf'
import {AppLanguageDropdown} from '#/components/AppLanguageDropdown'
import {Button, ButtonText} from '#/components/Button'
import {DiscordLogo} from '#/components/icons/DiscordLogo'
import {TimesLarge_Stroke2_Corner0_Rounded as TimesIcon} from '#/components/icons/Times'
import * as Layout from '#/components/Layout'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'

export const SplashScreen = ({
  onDismiss,
  onPressSignin,
  onPressCreateAccount,
}: {
  onDismiss?: () => void
  onPressSignin: () => void
  onPressCreateAccount: () => void
}) => {
  const sso = BRAND.sso
  const {_} = useLingui()
  const t = useTheme()
  const {isTabletOrMobile: IS_WEB_MOBILE} = useWebMediaQueries()
  const [showClipOverlay, setShowClipOverlay] = useState(false)

  useEffect(() => {
    const getParams = new URLSearchParams(window.location.search)
    const clip = getParams.get('clip')
    if (clip === 'true') {
      setShowClipOverlay(true)
      postAppClipMessage({
        action: 'present',
      })
    }
  }, [])

  const kawaii = useKawaiiMode()

  return (
    <>
      {onDismiss && (
        <Pressable
          accessibilityRole="button"
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            padding: 20,
            zIndex: 100,
          }}
          onPress={onDismiss}>
          <TimesIcon width={24} style={t.atoms.text} />
        </Pressable>
      )}

      <Layout.Center style={[a.h_full, a.flex_1]} ignoreTabletLayoutOffset>
        <View
          testID="noSessionView"
          style={[
            a.h_full,
            a.justify_center,
            // @ts-expect-error web only
            {paddingBottom: '20vh'},
            IS_WEB_MOBILE && a.pb_5xl,
            t.atoms.border_contrast_medium,
            a.align_center,
            a.gap_5xl,
            a.flex_1,
          ]}>
          <ErrorBoundary>
            <View style={[a.justify_center, a.align_center]}>
              <Logo width={kawaii ? 300 : 100} fill="sky" />

              {!kawaii && (
                <View style={[a.pt_2xl, a.align_center, a.gap_2xs]}>
                  <Text
                    style={[
                      a.font_bold,
                      a.text_center,
                      {fontSize: 44, lineHeight: 46, letterSpacing: -1},
                      t.atoms.text,
                    ]}>
                    {BRAND_NAME}
                  </Text>
                  <Text
                    style={[
                      a.text_md,
                      a.font_semi_bold,
                      a.text_center,
                      t.atoms.text_contrast_medium,
                    ]}>
                    <Trans>Your corner of the open social web</Trans>
                  </Text>
                </View>
              )}
            </View>

            <View
              testID="signinOrCreateAccount"
              style={[a.w_full, a.px_xl, a.gap_md, a.pb_2xl, {maxWidth: 440}]}>
              {/* Discord/Authentik SSO to the LEFT of Create account, matching
                  chat's "Continue with Discord". startCooeyLogin() hits delta
                  /auth/oidc/login?flow=atproto -> Authentik (Discord) -> hands
                  back an atproto session; identity is centralized at Authentik. */}
              <View style={[a.flex_row, a.gap_md]}>
                {sso && (
                  <Button
                    testID="discordSignInButton"
                    style={[a.flex_1, a.gap_sm, {backgroundColor: sso.background}]}
                    onPress={() => startCooeyLogin()}
                    label={_(msg`Continue with ${sso.label}`)}
                    accessibilityHint={_(
                      msg`Sign in with your ${BRAND_NAME} account`,
                    )}
                    size="large"
                    variant="solid"
                    color="primary">
                    {sso.icon === 'discord' ? <DiscordLogo /> : <BrandSsoIcon size={20} />}
                    <ButtonText style={[{color: '#fff'}]}>
                      {sso.label}
                    </ButtonText>
                  </Button>
                )}
                <Button
                  testID="createAccountButton"
                  style={[a.flex_1]}
                  onPress={onPressCreateAccount}
                  label={_(msg`Create new account`)}
                  accessibilityHint={_(
                    msg`Opens flow to create a new ${BRAND_NAME} account`,
                  )}
                  size="large"
                  variant="solid"
                  color="secondary">
                  <ButtonText>
                    <Trans>Create account</Trans>
                  </ButtonText>
                </Button>
              </View>
              <Button
                testID="signInButton"
                onPress={onPressSignin}
                label={_(msg`Sign in with a password`)}
                accessibilityHint={_(
                  msg`Opens flow to sign in with an app password`,
                )}
                size="large"
                variant="ghost"
                color="secondary">
                <ButtonText>
                  <Trans>Sign in with a password</Trans>
                </ButtonText>
              </Button>
            </View>
          </ErrorBoundary>
        </View>
        <Footer />
      </Layout.Center>
      <AppClipOverlay
        visible={showClipOverlay}
        setIsVisible={setShowClipOverlay}
      />
    </>
  )
}

function Footer() {
  const t = useTheme()
  const {_} = useLingui()

  return (
    <View
      style={[
        a.absolute,
        a.inset_0,
        {top: 'auto'},
        a.px_xl,
        a.py_lg,
        a.border_t,
        a.flex_row,
        a.align_center,
        a.flex_wrap,
        a.gap_xl,
        a.flex_1,
        t.atoms.border_contrast_medium,
      ]}>
      <InlineLinkText
        label={_(msg`Learn more about Bluesky`)}
        to="https://bsky.social">
        <Trans>Business</Trans>
      </InlineLinkText>
      <InlineLinkText
        label={_(msg`Read the Bluesky blog`)}
        to="https://bsky.social/about/blog">
        <Trans>Blog</Trans>
      </InlineLinkText>
      <InlineLinkText
        label={_(msg`See jobs at Bluesky`)}
        to="https://bsky.social/about/join">
        <Trans comment="Link to a page with job openings at Bluesky">
          Jobs
        </Trans>
      </InlineLinkText>

      <View style={a.flex_1} />

      <AppLanguageDropdown />
    </View>
  )
}
